<script>
import axios from "axios";
import PaystackPop from "@paystack/inline-js";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { mapGetters } from "vuex";
import logo from "@/assets/logo.png";

export default {
  name: "AppFinance",
  data: () => ({
    fees: [],
    receipts: [],
    loading: true,
    payingId: null,
    selectedReceipt: null,
    error: "",
    scholarships: [
      {
        id: 1,
        name: "FCP Scholarship",
        link: "https://firstcitypoly.edu.ng/funding",
      },
    ],
  }),
  computed: {
    ...mapGetters(["getUser", "getApplication", "getStudent"]),
    unpaidCharges() {
      return this.fees.filter((fee) => !fee.paid);
    },
    paidCharges() {
      const charges = this.receipts.map((receipt) => {
        const fee = this.fees.find(
          (item) => String(item.id) === String(receipt.paymentId)
        );
        return {
          ...fee,
          ...receipt,
          receipt,
          description: receipt.description || fee?.description,
          name: receipt.name || fee?.name,
        };
      });
      const receiptPaymentIds = new Set(
        this.receipts.map((receipt) => String(receipt.paymentId))
      );
      const paidWithoutReceipt = this.fees
        .filter((fee) => fee.paid && !receiptPaymentIds.has(String(fee.id)))
        .map((fee) => ({ ...fee, receipt: null }));
      return [...charges, ...paidWithoutReceipt];
    },
    fullName() {
      return [
        this.getUser?.firstName,
        this.getUser?.otherName,
        this.getUser?.lastName,
      ]
        .filter(Boolean)
        .join(" ");
    },
    displayNumber() {
      return (
        this.getStudent?.matriculationNumber ||
        this.getApplication?.applicationNumber ||
        "N/A"
      );
    },
  },
  async mounted() {
    await this.loadFees();
  },
  methods: {
    money(value) {
      return new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGN",
      }).format(Number(value) || 0);
    },
    formatDate(value) {
      if (!value) return "N/A";
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? "N/A"
        : date.toLocaleDateString("en-NG", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
    },
    async loadFees() {
      this.loading = true;
      this.error = "";
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/student-payments/me`
        );
        this.fees = response.data?.data?.fees || [];
        this.receipts = response.data?.data?.receipts || [];
      } catch (error) {
        this.error = error.response?.data?.message || "Unable to load fees.";
      } finally {
        this.loading = false;
      }
    },
    async pay(charge) {
      this.payingId = charge.id;
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/student-payments/initialize`,
          { paymentId: charge.id }
        );
        const { accessCode } = response.data.data;
        new PaystackPop().resumeTransaction(accessCode, {
          onSuccess: async ({ reference }) => {
            try {
              const verification = await axios.post(
                `${process.env.VUE_APP_API_BASE_URL}/student-payments/verify`,
                { paymentRef: reference }
              );
              const enrollment = verification.data.data?.enrollment;
              if (enrollment) {
                await Swal.fire(
                  "Enrolment completed",
                  `Your matriculation number is ${enrollment.matriculationNumber}. Sign in again with this number and the same password.`,
                  "success"
                );
                this.$store.commit("clearAuthentication");
                this.$router.push({ name: "login" });
                return;
              }
              await this.$store.dispatch("refreshSession");
              await this.loadFees();
              Swal.fire(
                "Payment verified",
                "Your payment has been recorded.",
                "success"
              );
            } catch (error) {
              Swal.fire(
                "Verification pending",
                error.response?.data?.message ||
                  "We will reconcile this payment automatically.",
                "warning"
              );
            }
          },
          onCancel: () => {
            this.payingId = null;
          },
        });
      } catch (error) {
        Swal.fire(
          "Payment unavailable",
          error.response?.data?.message || error.message,
          "error"
        );
      } finally {
        this.payingId = null;
      }
    },
    viewReceipt(charge) {
      this.selectedReceipt = { ...charge, ...(charge.receipt || {}) };
    },
    closeReceipt() {
      this.selectedReceipt = null;
    },
    downloadReceipt() {
      if (!this.selectedReceipt) return;
      const receipt = this.selectedReceipt;
      const doc = new jsPDF({ compress: true });

      // Preserve the original FCP receipt presentation while sourcing all
      // values from the canonical, verified payment record.
      doc.addImage(logo, "PNG", 15, 18, 30, 20, "fcp-logo", "FAST");
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 51, 153);
      doc.text(
        (
          process.env.VUE_APP_FCP_NAME || "First City Polytechnic"
        ).toUpperCase(),
        105,
        25,
        { align: "center" }
      );

      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(128, 128, 128);
      doc.text(process.env.VUE_APP_FCP_ADDRESS || "", 105, 32, {
        align: "center",
      });
      doc.text(
        `Tel: ${process.env.VUE_APP_FCP_PHONE_NO || ""} | Email: ${
          process.env.VUE_APP_FCP_INFO_EMAIL || ""
        }`,
        105,
        37,
        { align: "center" }
      );

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 51, 153);
      doc.text("Payment Receipt", 105, 60, { align: "center" });

      const startY = 75;
      const lineHeight = 8;
      const reference = receipt.paymentRef || "N/A";
      const identifierLabel = this.getStudent
        ? "Matriculation Number"
        : "Application Number";

      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 0);
      doc.text(`Receipt Number: ${reference}`, 15, startY);
      doc.text(
        `Date: ${this.formatDate(receipt.paymentDate)}`,
        15,
        startY + lineHeight
      );
      doc.text(
        `Payment Method: ${receipt.paymentChannel || "Online Payment"}`,
        15,
        startY + lineHeight * 2
      );
      doc.text(
        `Student Name: ${(this.fullName || "N/A").toUpperCase()}`,
        15,
        startY + lineHeight * 4
      );
      doc.text(
        `${identifierLabel}: ${this.displayNumber}`,
        15,
        startY + lineHeight * 5
      );

      const amount = Number(receipt.amount) || 0;
      const formattedAmount = `NGN ${amount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
      doc.autoTable({
        startY: startY + lineHeight * 7,
        head: [["Description", "Amount"]],
        body: [
          [receipt.description || receipt.name || "Payment", formattedAmount],
        ],
        theme: "grid",
        styles: { fontSize: 10 },
        headStyles: {
          fillColor: [0, 51, 153],
          textColor: 255,
          halign: "center",
          valign: "middle",
        },
      });

      const afterTableY = doc.lastAutoTable.finalY + 10;
      doc.setFont("helvetica", "normal");
      doc.text(`Total Paid: ${formattedAmount}`, 15, afterTableY);

      const messageY = afterTableY + 20;
      doc.setFont("helvetica", "italic");
      doc.text("Thank you for your payment.", 15, messageY);
      const contactMessage = `If you have any questions, please contact us at ${
        process.env.VUE_APP_FCP_PAYMENTS_EMAIL || "the Bursary Unit"
      }.`;
      doc.text(doc.splitTextToSize(contactMessage, 180), 15, messageY + 6);
      doc.text(
        "We appreciate your prompt payment and look forward to supporting your academic journey!",
        15,
        messageY + 18
      );

      doc.save(`FCP_Receipt_${reference}.pdf`);
    },
  },
};
</script>

<template>
  <div class="container-fluid p-4 text-start">
    <div class="d-flex justify-content-between align-items-center">
      <h4 class="fw-bold mb-0">Finance</h4>
      <button
        class="btn btn-outline-primary btn-sm"
        :disabled="loading"
        @click="loadFees"
      >
        <i class="bi bi-arrow-clockwise"></i> Refresh
      </button>
    </div>
    <hr />

    <div v-if="error" class="alert alert-danger">{{ error }}</div>
    <div v-if="loading" class="text-center text-muted py-5">
      <div class="spinner-border spinner-border-sm me-2"></div>
      Loading payment information…
    </div>

    <div v-else class="row justify-content-center">
      <div class="col-md-10 col-lg-8 col-xl-7">
        <section class="mb-5">
          <h6 class="fw-bold">Unpaid Charges</h6>
          <div v-if="!unpaidCharges.length" class="alert alert-success">
            <i class="bi bi-check-circle-fill me-2"></i>
            There are no outstanding charges.
          </div>
          <div
            v-for="charge in unpaidCharges"
            :key="charge.id"
            class="card border-0 shadow-sm mb-3"
          >
            <div
              class="card-body d-flex justify-content-between gap-3 align-items-center"
            >
              <div>
                <h6 class="mb-1">{{ charge.description || charge.name }}</h6>
                <div class="small text-muted">
                  <span v-if="charge.blockedReason">{{
                    charge.blockedReason
                  }}</span>
                  <span v-else-if="charge.dueDate">
                    Due {{ formatDate(charge.dueDate) }}
                  </span>
                  <span v-else>{{
                    charge.payable
                      ? "Ready for payment"
                      : "Complete the previous stage first"
                  }}</span>
                </div>
              </div>
              <div class="text-end flex-shrink-0">
                <div class="fw-bold mb-2">{{ money(charge.amount) }}</div>
                <button
                  class="btn btn-primary btn-sm"
                  :disabled="!charge.payable || payingId === charge.id"
                  @click="pay(charge)"
                >
                  {{ payingId === charge.id ? "Starting…" : "Pay Now" }}
                </button>
              </div>
            </div>
          </div>
        </section>

        <section class="mb-5">
          <h6 class="fw-bold">Paid Charges</h6>
          <div v-if="!paidCharges.length" class="card border-0 shadow-sm">
            <div class="card-body text-muted">No verified payments yet.</div>
          </div>
          <div
            v-for="charge in paidCharges"
            :key="charge.id"
            class="card border-0 shadow-sm mb-3"
          >
            <div
              class="card-body d-flex justify-content-between gap-3 align-items-center"
            >
              <div>
                <h6 class="mb-1">{{ charge.description || charge.name }}</h6>
                <span class="badge text-bg-success">Paid</span>
              </div>
              <div class="text-end">
                <div class="fw-bold text-success mb-2">
                  {{ money(charge.amount) }}
                </div>
                <button
                  class="btn btn-outline-dark btn-sm"
                  @click="viewReceipt(charge)"
                >
                  <i class="bi bi-receipt me-1"></i> View Receipt
                </button>
              </div>
            </div>
          </div>
        </section>

        <section id="scholarships" class="mb-5 scroll-target">
          <h6 class="fw-bold">Scholarships & Funding</h6>
          <div class="card border-0 shadow-sm">
            <div
              v-for="scholarship in scholarships"
              :key="scholarship.id"
              class="card-body d-flex justify-content-between align-items-center"
            >
              <span>{{ scholarship.name }}</span>
              <a
                :href="scholarship.link"
                target="_blank"
                rel="noopener noreferrer"
                class="btn btn-outline-primary btn-sm"
              >
                Learn More
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>

    <div
      v-if="selectedReceipt"
      class="receipt-backdrop"
      @click.self="closeReceipt"
    >
      <div class="card border-0 shadow receipt-panel">
        <div
          class="card-header bg-white d-flex justify-content-between align-items-center"
        >
          <h5 class="mb-0 fw-bold">Payment Receipt</h5>
          <button
            class="btn-close"
            aria-label="Close"
            @click="closeReceipt"
          ></button>
        </div>
        <div class="card-body">
          <!-- <div class="text-center mb-4">
            <img :src="logo" alt="First City Polytechnic" width="75" />
            <h6 class="mt-2 mb-0 text-capitalize">{{ fullName }}</h6>
            <small class="text-muted">{{ displayNumber }}</small>
          </div> -->
          <dl class="row small mb-0">
            <dt class="col-5">Payment</dt>
            <dd class="col-7">
              {{ selectedReceipt.description || selectedReceipt.name }}
            </dd>
            <dt class="col-5">Amount</dt>
            <dd class="col-7">{{ money(selectedReceipt.amount) }}</dd>
            <dt class="col-5">Reference</dt>
            <dd class="col-7 text-break">
              {{ selectedReceipt.paymentRef || "N/A" }}
            </dd>
            <dt class="col-5">Date</dt>
            <dd class="col-7">{{ formatDate(selectedReceipt.paymentDate) }}</dd>
            <dt class="col-5">Payment Method</dt>
            <dd class="col-7 text-capitalize">
              {{ selectedReceipt.paymentChannel || "N/A" }}
            </dd>
            <dt class="col-5">Status</dt>
            <dd class="col-7 text-success fw-bold">Successful</dd>
          </dl>
        </div>
        <div class="card-footer bg-white text-end">
          <button class="btn btn-primary btn-sm" @click="downloadReceipt">
            <i class="bi bi-download me-1"></i> Download PDF
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.receipt-backdrop {
  position: fixed;
  z-index: 1080;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.55);
}

.receipt-panel {
  width: min(100%, 520px);
  max-height: 90vh;
  overflow: auto;
}

.scroll-target {
  scroll-margin-top: 90px;
}
</style>
